import * as React from 'react';
import { useColorField, useObjectRef } from 'react-aria';
import {
    ColorPickerStateContext,
    type ColorFieldProps as RACColorSliderProps,
} from 'react-aria-components';
import { useColorFieldState } from 'react-stately';

import { assert } from '@gitbook/util-ts';

import { TextInput, type TextInputProps } from '../TextInput';
import { ColorSwatch } from './ColorSwatch';

export type ColorFieldProps = Omit<RACColorSliderProps, 'size'> & {
    size?: TextInputProps['size'];
};

/**
 * A color field allows users to edit a hex color or individual color channel value.
 */
export const ColorField = React.forwardRef(function ColorField(
    props: ColorFieldProps,
    ref: React.ForwardedRef<HTMLInputElement>
) {
    const ctx = React.useContext(ColorPickerStateContext);
    assert(ctx, 'ColorField must be used within a ColorPicker');
    const { color, setColor } = ctx;
    const state = useColorFieldState({
        value: color,
        onChange: setColor,
    });
    const inputRef = useObjectRef(ref);
    const {
        inputProps: { size, onCopy: _onCopy, onSubmit: _onSubmit, ...inputProps },
    } = useColorField(props, state, inputRef);

    return (
        <TextInput
            ref={inputRef}
            prefix={<ColorSwatch size={16} />}
            size={props.size}
            {...inputProps}
            inputClassName="w-0"
            onKeyDownCapture={(event) => {
                inputProps.onKeyDownCapture?.(event);
                if (event.key === 'Enter') {
                    if (!state.validate(state.inputValue) || state.inputValue.length !== 7) {
                        event.preventDefault();
                        event.stopPropagation();
                        state.commit();
                    }
                }
            }}
        />
    );
});
